import React from 'react';
import AdminPageLoader from '../../../shared/ui/admin-loader/AdminPageLoader';
import { usePlacementTestForm } from '../lib/usePlacementTestForm';
import { getLocalizedText, isTextQuestion, summarizeQuestion } from '../lib/questionText';
import QuestionEditor from './QuestionEditor';

const field = {
  width: '100%',
  padding: '9px 10px',
  border: '1px solid #D9D9D9',
  borderRadius: '6px',
  fontFamily: 'inherit',
  fontSize: '13px',
};

const labelStyle = {
  fontSize: '12px',
  fontWeight: 600,
  color: '#475569',
  display: 'block',
  marginBottom: '4px',
};

const hint = { fontSize: '11px', color: '#94A3B8', margin: '4px 0 0' };

// Подписи словами вместо иконок: заполняют тест не технические люди,
// значки ⧉ и ✕ им ничего не говорят.
const iconButton = {
  padding: '5px 10px',
  border: '1px solid #E2E8F0',
  background: '#fff',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '12px',
  fontWeight: 600,
  lineHeight: '18px',
  color: '#475569',
  fontFamily: 'inherit',
};

const Collapsible = ({ title, subtitle, defaultOpen = false, children }) => {
  const [open, setOpen] = React.useState(defaultOpen);

  return (
    <div style={{ border: '1px solid #E2E8F0', borderRadius: '10px', marginBottom: '14px', background: '#fff' }}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '14px 18px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <span style={{ fontSize: '12px', color: '#94A3B8' }}>{open ? '▾' : '▸'}</span>
        <span style={{ fontSize: '15px', fontWeight: 600, color: '#0F172A' }}>{title}</span>
        {subtitle && <span style={{ fontSize: '12px', color: '#94A3B8' }}>{subtitle}</span>}
      </button>
      {open && <div style={{ padding: '0 18px 18px' }}>{children}</div>}
    </div>
  );
};

const LocalizedRow = ({ title, value, onChange, rows }) => (
  <div style={{ marginBottom: '14px' }}>
    <span style={labelStyle}>{title}</span>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
      {['ua', 'en'].map((locale) => (
        <div key={locale}>
          <span style={{ fontSize: '10px', color: '#94A3B8' }}>
            {locale === 'ua' ? 'Українська' : 'English'}
          </span>
          {rows ? (
            <textarea
              rows={rows}
              value={value?.[locale] || ''}
              onChange={(e) => onChange({ ...value, [locale]: e.target.value })}
              style={{ ...field, marginTop: '2px' }}
            />
          ) : (
            <input
              type="text"
              value={value?.[locale] || ''}
              onChange={(e) => onChange({ ...value, [locale]: e.target.value })}
              style={{ ...field, marginTop: '2px' }}
            />
          )}
        </div>
      ))}
    </div>
  </div>
);

const NumberField = ({ title, value, onChange, step = '1', suffix }) => (
  <div>
    <span style={labelStyle}>{title}</span>
    <input
      type="number"
      step={step}
      value={value ?? ''}
      onChange={(e) => {
        const parsed = parseFloat(e.target.value);
        onChange(Number.isFinite(parsed) ? parsed : 0);
      }}
      style={field}
    />
    {suffix && <p style={hint}>{suffix}</p>}
  </div>
);

// Сильні сторони й зони росту перекладені, тож редагуються двома колонками:
// рядок ліворуч і рядок праворуч на тій самій позиції — це один пункт.
const LocalizedLinesField = ({ title, value, onChange, rows = 3 }) => {
  const items = Array.isArray(value) ? value : [];
  const side = (locale) => items.map((item) => item?.[locale] || '').join('\n');

  const setSide = (locale, raw) => {
    const other = locale === 'ua' ? 'en' : 'ua';
    const lines = raw.split('\n');
    const length = Math.max(lines.length, items.length);
    const next = [];

    for (let index = 0; index < length; index += 1) {
      const mine = lines[index] ?? '';
      const theirs = items[index]?.[other] || '';

      if (!mine.trim() && !theirs.trim()) continue;

      next.push(locale === 'ua' ? { ua: mine, en: theirs } : { ua: theirs, en: mine });
    }

    onChange(next);
  };

  return (
    <div style={{ marginBottom: '14px' }}>
      <span style={labelStyle}>{title}</span>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
        {['ua', 'en'].map((locale) => (
          <div key={locale}>
            <span style={{ fontSize: '10px', color: '#94A3B8' }}>
              {locale === 'ua' ? 'Українська' : 'English'}
            </span>
            <textarea
              rows={rows}
              value={side(locale)}
              onChange={(e) => setSide(locale, e.target.value)}
              style={{ ...field, marginTop: '2px' }}
            />
          </div>
        ))}
      </div>
      <p style={hint}>Один рядок — один пункт. Рядки ліворуч і праворуч мають відповідати один одному.</p>
    </div>
  );
};

const LinesField = ({ title, value, onChange, placeholder, rows = 3 }) => (
  <div style={{ marginBottom: '14px' }}>
    <span style={labelStyle}>{title}</span>
    <textarea
      rows={rows}
      value={(value || []).join('\n')}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value.split('\n').map((line) => line.trim()).filter(Boolean))}
      style={field}
    />
  </div>
);

const buildQuestionId = (pool) => {
  const used = new Set(pool.map((question) => question.id));
  let index = pool.length + 1;

  while (used.has(`q${index}`)) {
    index += 1;
  }

  return `q${index}`;
};

// formId нужен, чтобы кнопка "Зберегти" за пределами формы (её рисует
// страница) знала, какую именно форму отправлять: на странице детского теста
// и взрослого это разные формы.
const PlacementTestForm = ({
  initialData,
  templateId,
  formId = 'placement-test-form',
  fallbackSlug,
  saveTemplate,
}) => {
  const formik = usePlacementTestForm(initialData, templateId, {
    fallbackSlug,
    saveTemplate,
  });
  const pool = React.useMemo(() => formik.values.questionPool || [], [formik.values.questionPool]);
  const [activeIndex, setActiveIndex] = React.useState(0);

  const setPool = (nextPool) => formik.setFieldValue('questionPool', nextPool);

  const totals = pool.reduce(
    (accumulator, question) => {
      const summary = summarizeQuestion(question);
      return {
        gaps: accumulator.gaps + summary.count,
        score: accumulator.score + summary.score,
        incomplete: accumulator.incomplete + summary.incomplete,
      };
    },
    { gaps: 0, score: 0, incomplete: 0 }
  );

  const activeQuestion = pool[activeIndex] || null;
  const questionCount = formik.values.settings?.questionCount ?? 0;

  const moveQuestion = (from, to) => {
    if (to < 0 || to >= pool.length) return;
    const next = [...pool];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setPool(next);
    setActiveIndex(to);
  };

  const duplicateQuestion = (index) => {
    const source = pool[index];
    const newId = buildQuestionId(pool);
    const copy = JSON.parse(JSON.stringify(source));

    copy.id = newId;
    copy.groups = (copy.groups || []).map((group, groupIndex) => ({
      ...group,
      id: `${newId}-g${groupIndex + 1}`,
      options: group.options?.map((option, optionIndex) => ({
        ...option,
        id: `${newId}-g${groupIndex + 1}-o${optionIndex + 1}`,
      })),
    }));

    const next = [...pool];
    next.splice(index + 1, 0, copy);
    setPool(next);
    setActiveIndex(index + 1);
  };

  const removeQuestion = (index) => {
    if (!window.confirm(`Видалити завдання ${index + 1}?`)) return;
    const next = pool.filter((_, i) => i !== index);
    setPool(next);
    setActiveIndex(Math.max(0, Math.min(index, next.length - 1)));
  };

  const addQuestion = (type) => {
    const id = buildQuestionId(pool);
    const groupId = `${id}-g1`;
    const question =
      type === 'text_input_group'
        ? {
            id,
            type,
            prompt: { ua: '', en: '' },
            helperText: null,
            groups: [
              { id: groupId, label: { ua: '', en: '' }, inputKind: 'short', weight: 1, acceptedAnswers: [] },
            ],
          }
        : {
            id,
            type: 'single_choice_group',
            prompt: { ua: '', en: '' },
            helperText: null,
            groups: [
              {
                id: groupId,
                label: { ua: '', en: '' },
                weight: 1,
                options: ['A', 'B'].map((optionLabel, optionIndex) => ({
                  id: `${groupId}-o${optionIndex + 1}`,
                  label: optionLabel,
                  text: { ua: '', en: '' },
                  isCorrect: optionIndex === 0,
                })),
              },
            ],
          };

    setPool([...pool, question]);
    setActiveIndex(pool.length);
  };

  return (
    <form id={formId} onSubmit={formik.handleSubmit} className="programs-create-form">
      {formik.isSubmitting && <AdminPageLoader label="Збереження шаблону..." fullScreen />}

      {/* Сводка: сколько всего заданий, пропусков и баллов */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 5,
          display: 'flex',
          flexWrap: 'wrap',
          gap: '18px',
          alignItems: 'center',
          padding: '12px 18px',
          marginBottom: '14px',
          background: '#081249',
          borderRadius: '10px',
          color: '#fff',
          fontSize: '13px',
        }}
      >
        <span><strong>{pool.length}</strong> завдань</span>
        <span><strong>{totals.gaps}</strong> пропусків / питань</span>
        <span><strong>{totals.score}</strong> балів максимум</span>
        {totals.incomplete > 0 && (
          <span style={{ color: '#FFD9A0' }}>
            ⚠ без правильної відповіді: {totals.incomplete}
          </span>
        )}
        {questionCount > 0 && questionCount < pool.length && (
          <span style={{ color: '#FFD9A0' }}>
            ⚠ студенту показується лише {questionCount} з {pool.length} — змініть «Кількість питань у тесті»
          </span>
        )}
      </div>

      <Collapsible title="Основне" subtitle="назва, slug, активність">
        <LocalizedRow
          title="Назва шаблону"
          value={formik.values.title}
          onChange={(title) => formik.setFieldValue('title', title)}
        />
        <div style={{ marginBottom: '14px' }}>
          <span style={labelStyle}>Slug (ідентифікатор)</span>
          <input
            type="text"
            value={formik.values.slug?.en || formik.values.slug?.ua || ''}
            onChange={(e) => formik.setFieldValue('slug', { ua: e.target.value, en: e.target.value })}
            style={field}
          />
          <p style={hint}>Сайт шукає тест саме за ним — змінювати лише свідомо.</p>
        </div>
        <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={formik.values.isActive !== false}
            onChange={(e) => formik.setFieldValue('isActive', e.target.checked)}
          />
          Активний шаблон
        </label>
      </Collapsible>

      <Collapsible title="Вступ і налаштування" subtitle="що бачить студент до старту">
        <p style={{ ...hint, margin: '0 0 12px', fontSize: '13px', color: '#475569' }}>
          Ці чотири числа показуються студенту на першому екрані тесту («N запитань · ~M хвилин»)
          і в рядку «Орієнтовно триває…». Якщо «від» і «до» однакові — виводиться одне число.
          Скільки завдань реально видається, задає поле «Кількість питань у тесті» нижче.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '14px' }}>
          <NumberField
            title="Питань від"
            value={formik.values.intro?.questionCountMin}
            onChange={(value) => formik.setFieldValue('intro.questionCountMin', value)}
          />
          <NumberField
            title="Питань до"
            value={formik.values.intro?.questionCountMax}
            onChange={(value) => formik.setFieldValue('intro.questionCountMax', value)}
          />
          <NumberField
            title="Час від (хв)"
            value={formik.values.intro?.estimatedTimeMin}
            onChange={(value) => formik.setFieldValue('intro.estimatedTimeMin', value)}
          />
          <NumberField
            title="Час до (хв)"
            value={formik.values.intro?.estimatedTimeMax}
            onChange={(value) => formik.setFieldValue('intro.estimatedTimeMax', value)}
          />
        </div>

        <LinesField
          title="Переваги тесту (один пункт на рядок)"
          value={formik.values.intro?.benefits}
          onChange={(value) => formik.setFieldValue('intro.benefits', value)}
          placeholder={'Точна діагностика рівня\nМиттєвий результат з розбором'}
        />

        <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: '18px', alignItems: 'start' }}>
          <NumberField
            title="Кількість питань у тесті"
            value={formik.values.settings?.questionCount}
            onChange={(value) => formik.setFieldValue('settings.questionCount', value)}
            suffix={`У банку: ${pool.length}`}
          />
          <div style={{ display: 'flex', gap: '18px', paddingTop: '20px' }}>
            <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formik.values.settings?.randomizeQuestions ?? false}
                onChange={(e) => formik.setFieldValue('settings.randomizeQuestions', e.target.checked)}
              />
              Перемішувати питання
            </label>
            <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formik.values.settings?.randomizeOptions ?? false}
                onChange={(e) => formik.setFieldValue('settings.randomizeOptions', e.target.checked)}
              />
              Перемішувати варіанти
            </label>
          </div>
        </div>
      </Collapsible>

      <Collapsible
        title="Шкала CEFR"
        subtitle={`${(formik.values.cefrScale || []).length} рівнів · за балами`}
      >
        {(formik.values.cefrScale || []).map((level, index) => (
          <div
            key={index}
            style={{ padding: '14px', background: '#F8FAFC', borderRadius: '8px', marginBottom: '12px' }}
          >
            <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr 90px 90px 28px', gap: '10px', marginBottom: '10px' }}>
              <div>
                <span style={labelStyle}>Рівень</span>
                <input
                  type="text"
                  value={level.level || ''}
                  onChange={(e) => formik.setFieldValue(`cefrScale.${index}.level`, e.target.value)}
                  style={field}
                />
              </div>
              <div>
                <span style={labelStyle}>Назва (UA / EN)</span>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                  {['ua', 'en'].map((locale) => (
                    <input
                      key={locale}
                      type="text"
                      value={level.title?.[locale] || ''}
                      onChange={(e) =>
                        formik.setFieldValue(`cefrScale.${index}.title`, {
                          ...level.title,
                          [locale]: e.target.value,
                        })
                      }
                      style={field}
                    />
                  ))}
                </div>
              </div>
              <NumberField
                title="Від балів"
                value={level.minScore}
                onChange={(value) => formik.setFieldValue(`cefrScale.${index}.minScore`, value)}
              />
              <NumberField
                title="До балів"
                value={level.maxScore}
                onChange={(value) => formik.setFieldValue(`cefrScale.${index}.maxScore`, value)}
              />
              <button
                type="button"
                title="Видалити рівень"
                onClick={() =>
                  formik.setFieldValue(
                    'cefrScale',
                    formik.values.cefrScale.filter((_, i) => i !== index)
                  )
                }
                style={{ ...iconButton, alignSelf: 'end', color: '#FE0707', height: '35px' }}
              >
                ✕
              </button>
            </div>

            <LocalizedRow
              title="Опис результату"
              value={level.summary}
              rows={2}
              onChange={(summary) => formik.setFieldValue(`cefrScale.${index}.summary`, summary)}
            />

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <LocalizedLinesField
                title="Сильні сторони"
                value={level.strengths}
                rows={3}
                onChange={(value) => formik.setFieldValue(`cefrScale.${index}.strengths`, value)}
              />
              <LocalizedLinesField
                title="Зони росту"
                value={level.growthAreas}
                rows={3}
                onChange={(value) => formik.setFieldValue(`cefrScale.${index}.growthAreas`, value)}
              />
            </div>

            {/* Сервер вимагає курс для кожного рівня — без цих полів новий
                рівень неможливо було зберегти. */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
              {[
                { key: 'code', title: 'Код курсу' },
                { key: 'url', title: 'Посилання на курс *' },
              ].map(({ key, title }) => (
                <div key={key}>
                  <span style={labelStyle}>{title}</span>
                  <input
                    type="text"
                    value={level.recommendedCourse?.[key] || ''}
                    onChange={(e) =>
                      formik.setFieldValue(`cefrScale.${index}.recommendedCourse`, {
                        ...(level.recommendedCourse || {}),
                        [key]: e.target.value,
                      })
                    }
                    style={field}
                  />
                </div>
              ))}
              <div>
                <span style={labelStyle}>Назва курсу * (UA / EN)</span>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                  {['ua', 'en'].map((locale) => (
                    <input
                      key={locale}
                      type="text"
                      value={level.recommendedCourse?.name?.[locale] || ''}
                      onChange={(e) =>
                        formik.setFieldValue(`cefrScale.${index}.recommendedCourse`, {
                          ...(level.recommendedCourse || {}),
                          name: {
                            ...(level.recommendedCourse?.name || {}),
                            [locale]: e.target.value,
                          },
                        })
                      }
                      style={field}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}

        <button
          type="button"
          onClick={() =>
            formik.setFieldValue('cefrScale', [
              ...(formik.values.cefrScale || []),
              {
                level: '',
                title: '',
                minScore: 0,
                maxScore: 0,
                summary: { ua: '', en: '' },
                strengths: [],
                growthAreas: [],
                recommendedCourse: { code: '', name: '', url: '' },
              },
            ])
          }
          style={{
            padding: '8px 16px',
            background: '#2D9E6B',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: 600,
          }}
        >
          + Додати рівень
        </button>
      </Collapsible>

      {/* Завдання: зліва список, справа редактор одного завдання */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(220px, 280px) 1fr',
          gap: '16px',
          alignItems: 'start',
        }}
      >
        <div
          style={{
            position: 'sticky',
            top: '70px',
            border: '1px solid #E2E8F0',
            borderRadius: '10px',
            background: '#fff',
            padding: '10px',
            maxHeight: 'calc(100vh - 120px)',
            overflowY: 'auto',
          }}
        >
          <p style={{ ...labelStyle, padding: '4px 6px' }}>Завдання</p>

          {pool.map((question, index) => {
            const summary = summarizeQuestion(question);
            const active = index === activeIndex;

            return (
              <div
                key={question.id || index}
                style={{
                  padding: '8px',
                  marginBottom: '4px',
                  borderRadius: '8px',
                  background: active ? '#EEF4FF' : 'transparent',
                  border: `1px solid ${active ? '#C7D8FF' : 'transparent'}`,
                }}
              >
                <button
                  type="button"
                  onClick={() => setActiveIndex(index)}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                  }}
                >
                  <span style={{ fontSize: '12px', fontWeight: 700, color: '#0F172A' }}>
                    {index + 1}. {getLocalizedText(question.prompt) || 'Без назви'}
                  </span>
                  <span style={{ display: 'block', fontSize: '11px', color: '#64748B', marginTop: '2px' }}>
                    {isTextQuestion(question) ? 'Пропуски' : 'Вибір'} · {summary.count} · {summary.score} б
                    {summary.incomplete > 0 && (
                      <span style={{ color: '#B45309' }}> · ⚠ {summary.incomplete}</span>
                    )}
                  </span>
                </button>

                {active && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                    <button type="button" style={iconButton} onClick={() => moveQuestion(index, index - 1)}>
                      Вище
                    </button>
                    <button type="button" style={iconButton} onClick={() => moveQuestion(index, index + 1)}>
                      Нижче
                    </button>
                    <button type="button" style={iconButton} onClick={() => duplicateQuestion(index)}>
                      Копіювати
                    </button>
                    <button
                      type="button"
                      style={{ ...iconButton, color: '#B91C1C', borderColor: '#FECACA' }}
                      onClick={() => removeQuestion(index)}
                    >
                      Видалити
                    </button>
                  </div>
                )}
              </div>
            );
          })}

          <div style={{ display: 'grid', gap: '6px', marginTop: '10px' }}>
            <button
              type="button"
              onClick={() => addQuestion('text_input_group')}
              style={{ ...iconButton, padding: '8px', background: '#F1F5F9', fontWeight: 600 }}
            >
              + Завдання з пропусками
            </button>
            <button
              type="button"
              onClick={() => addQuestion('single_choice_group')}
              style={{ ...iconButton, padding: '8px', background: '#F1F5F9', fontWeight: 600 }}
            >
              + Завдання з вибором
            </button>
          </div>
        </div>

        <div>
          {activeQuestion ? (
            <QuestionEditor
              key={activeQuestion.id || activeIndex}
              question={activeQuestion}
              index={activeIndex}
              onChange={(nextQuestion) =>
                setPool(pool.map((question, i) => (i === activeIndex ? nextQuestion : question)))
              }
            />
          ) : (
            <div style={{ padding: '40px', textAlign: 'center', color: '#94A3B8', border: '1px dashed #E2E8F0', borderRadius: '10px' }}>
              Завдань поки немає — додайте перше зліва.
            </div>
          )}
        </div>
      </div>

      {Object.keys(formik.errors).length > 0 && (
        <div
          style={{
            marginTop: '18px',
            padding: '14px 16px',
            background: '#FEF2F2',
            border: '1px solid #FCA5A5',
            borderRadius: '8px',
            fontSize: '13px',
            color: '#B91C1C',
          }}
        >
          <strong>Помилки валідації:</strong>
          <ul style={{ margin: '8px 0 0', paddingLeft: '20px' }}>
            {Object.entries(formik.errors).map(([key, value]) => (
              <li key={key}>
                {key}: {typeof value === 'string' ? value : JSON.stringify(value)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </form>
  );
};

export default PlacementTestForm;
